import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { getAnimeById } from "@/utils/anime";

const navItems = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category" },
  { label: "最新", to: "/latest" },
  { label: "热门", to: "/popular" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [favoriteOpen, setFavoriteOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavorites();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setFavoriteOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(`/search?query=${encodeURIComponent(value)}`);
  };

  const favoriteItems = favorites
    .map((id) => getAnimeById(id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .slice(0, 4);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-background-50/40 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12 h-16 md:h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
          <span className="w-9 h-9 rounded-md bg-primary-600 text-foreground-950 font-heading text-lg flex items-center justify-center border border-accent-500/40">
            色
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl font-semibold tracking-wide text-foreground-950">
              色情动漫
            </span>
            <span className="text-[10px] tracking-[0.28em] text-accent-500/90 mt-1">ANIME ARCHIVE</span>
          </span>
          <span className="ml-1 hidden sm:inline-flex items-center h-5 px-1.5 rounded border border-accent-600/60 text-[10px] font-label tracking-widest text-accent-400">
            18+
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 h-full">
          {navItems.map((item) => {
            const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative h-16 flex items-center text-sm tracking-wide transition-colors whitespace-nowrap ${
                  active ? "text-accent-400" : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item.label}
                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-500" />}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => {
              setSearchOpen((prev) => !prev);
              setFavoriteOpen(false);
            }}
            aria-label="搜索作品"
            className="w-9 h-9 rounded-md border border-background-300/70 text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center justify-center cursor-pointer"
          >
            <i className="ri-search-line text-lg" />
          </button>

          <div className="relative hidden sm:block">
            <button
              type="button"
              onClick={() => setFavoriteOpen((prev) => !prev)}
              aria-label="我的收藏"
              className="h-9 px-3 rounded-md border border-background-300/70 text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-heart-3-line text-base" />
              <span className="text-xs font-label tracking-wider">{favorites.length}</span>
            </button>
            {favoriteOpen && (
              <div className="absolute right-0 top-11 w-72 rounded-lg border border-background-300/70 bg-background-100 p-3 animate-soft-fade">
                <p className="text-xs text-foreground-500 mb-2 px-1">本地收藏 · 仅保存在当前浏览器</p>
                {favoriteItems.length === 0 ? (
                  <p className="text-sm text-foreground-400 px-1 py-4">还没有收藏作品，去详情页点个心吧。</p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {favoriteItems.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/detail/${item.id}`}
                          className="flex items-center gap-2 rounded-md p-1.5 hover:bg-background-200/70 transition-colors"
                        >
                          <img src={item.cover} alt={item.title} className="w-9 h-12 object-cover object-top rounded" />
                          <span className="min-w-0">
                            <span className="block text-sm text-foreground-900 truncate">{item.title}</span>
                            <span className="block text-[11px] text-foreground-500">{item.category} · {item.year}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            className="lg:hidden w-9 h-9 rounded-md border border-background-300/70 text-foreground-300 flex items-center justify-center cursor-pointer"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200/70 bg-background-100/95 backdrop-blur-md">
          <form onSubmit={submitSearch} className="w-full px-4 md:px-8 lg:px-12 py-4 flex flex-col gap-3">
            <div className="flex items-center gap-2 bg-background-50 border border-background-300/70 rounded-md px-3 h-11">
              <i className="ri-search-2-line text-accent-500 text-lg" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品标题、标签，如：月下、悬疑、治愈"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="h-8 px-4 rounded-md bg-primary-600 hover:bg-primary-500 text-foreground-950 text-xs whitespace-nowrap transition-colors cursor-pointer"
              >
                搜索
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门搜索</span>
              {["月下", "悬疑", "校园", "成人向", "治愈"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => navigate(`/search?query=${encodeURIComponent(tag)}`)}
                  className="px-2.5 h-7 rounded-full border border-background-300/70 text-xs text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors cursor-pointer"
                >
                  {tag}
                </button>
              ))}
            </div>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200/70 bg-background-100/98 backdrop-blur-md">
          <nav className="px-4 py-3 flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="py-3 text-sm text-foreground-700 border-b border-background-200/60 last:border-b-0 flex items-center justify-between"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-500" />
              </Link>
            ))}
            <Link to="/search" className="py-3 text-sm text-accent-400 flex items-center gap-2">
              <i className="ri-search-line" />
              搜索全部作品
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}